// src/lib/user/userService.js
// All DB reads and writes for user preferences and display name.
// Never reads from or writes to localStorage.
// Every function is a no-op (returns null/void) when Supabase is unavailable.
//
// CRA SPA note: This project has no server-side API routes.
// Supabase RLS (auth.uid() = user_id) enforces owner-only access
// directly at the database layer — equivalent to a server-side auth check.

import { supabase, hasSupabase } from "../supabaseClient";

const TABLE = "user_preferences";

// ─────────────────────────────────────────────────────────────────────────────
// Key mapping helpers
// JS settings use camelCase; DB columns use snake_case.
// Only mapped keys are written — no accidental column injection.
// ─────────────────────────────────────────────────────────────────────────────

const PREFS_TO_DB = {
  uiMode:                 "ui_mode",
  accentColor:            "accent_color",
  fontSize:               "font_size",
  numberFormat:           "number_format",
  compactRows:            "compact_rows",
  hideBanner:             "hide_banner",
  showModuleDescriptions: "show_module_descriptions",
  timezone:               "timezone",
  riskFreeRate:           "risk_free_rate",
  benchmark:              "benchmark",
  portfolioValue:         "portfolio_value",
  pollIntervalMs:         "poll_interval_ms",
  alphaKey:               "alpha_key",
  profileSetupDone:       "profile_setup_done",
  welcomeGuideDone:       "welcome_guide_done",
  wmActiveLayers:         "wm_active_layers",
  workspaceWindows:       "workspace_windows",
  bio:                    "bio",
  country:                "country",
  goals:                  "investment_goals",
};

const DB_TO_PREFS = Object.fromEntries(
  Object.entries(PREFS_TO_DB).map(([js, db]) => [db, js])
);

// Convert a partial JS settings object → DB row (snake_case, alerts flattened)
function toDbRow(data) {
  const row = {};
  for (const [key, val] of Object.entries(data)) {
    if (key === "alerts" && val && typeof val === "object") {
      // Flatten nested alerts object into individual DB boolean columns
      if (val.earnings !== undefined) row.alert_earnings = val.earnings;
      if (val.geoRisk  !== undefined) row.alert_geo_risk  = val.geoRisk;
      if (val.price    !== undefined) row.alert_price     = val.price;
      if (val.macro    !== undefined) row.alert_macro     = val.macro;
      if (val.weekly   !== undefined) row.alert_weekly    = val.weekly;
    } else if (PREFS_TO_DB[key] !== undefined) {
      row[PREFS_TO_DB[key]] = val;
    }
    // Unknown keys are intentionally dropped
  }
  return row;
}

// Convert a DB row → JS settings object (camelCase, alerts re-nested)
function fromDbRow(row) {
  if (!row) return null;
  const out = { alerts: {} };
  for (const [col, val] of Object.entries(row)) {
    // Skip internal DB columns
    if (col === "user_id" || col === "created_at" || col === "updated_at") continue;
    if (col === "alert_earnings") { out.alerts.earnings = val; }
    else if (col === "alert_geo_risk")  { out.alerts.geoRisk  = val; }
    else if (col === "alert_price")     { out.alerts.price    = val; }
    else if (col === "alert_macro")     { out.alerts.macro    = val; }
    else if (col === "alert_weekly")    { out.alerts.weekly   = val; }
    else if (DB_TO_PREFS[col] !== undefined) {
      out[DB_TO_PREFS[col]] = val;
    }
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// getUserPreferences
// Returns the user's preferences in JS camelCase format, or null if not found.
// PGRST116 (row not found) is handled silently — returns null for new users
// who pre-date migration 005 and whose row hasn't been created yet.
// ─────────────────────────────────────────────────────────────────────────────
export async function getUserPreferences(userId) {
  if (!hasSupabase || !userId) return null;

  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("user_id", userId)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // row not found — expected for legacy users
    // Throw real errors (network, timeout, RLS) so callers can retry.
    throw new Error(`getUserPreferences: ${error.message}`);
  }

  return fromDbRow(data);
}

// ─────────────────────────────────────────────────────────────────────────────
// upsertUserPreferences
// Writes a partial settings update. Accepts JS camelCase keys.
// Converts to DB columns and upserts — creates the row if it doesn't exist,
// updates only the supplied fields if it does.
//
// Also handles displayName separately → profiles.display_name
// so callers can pass { displayName: "..." } in the same call.
//
// Re-throws on error so the hook can show an error state.
// ─────────────────────────────────────────────────────────────────────────────
export async function upsertUserPreferences(userId, data) {
  if (!hasSupabase || !userId || !data) return;

  const { displayName, ...rest } = data;

  // ── Write display_name to profiles ───────────────────────────────────────
  if (displayName !== undefined) {
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: displayName, updated_at: new Date().toISOString() })
      .eq("id", userId);
    if (error) {
      console.warn("[userService] upsertUserPreferences display_name error:", error.message);
      throw error;
    }
  }

  // ── Write all other prefs to user_preferences ────────────────────────────
  const dbRow = toDbRow(rest);
  if (Object.keys(dbRow).length === 0) return; // nothing to write

  const { error } = await supabase
    .from(TABLE)
    .upsert(
      { user_id: userId, ...dbRow, updated_at: new Date().toISOString() },
      { onConflict: "user_id" }
    );

  if (error) {
    console.warn("[userService] upsertUserPreferences error:", error.message);
    throw error;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ensureUserPreferences
// Called on every login via useAuth.js.
//
// Decision tree:
//   Row exists           → do nothing (no-op via ON CONFLICT DO NOTHING)
//   Row missing + new    → DB trigger already created it (won't reach here)
//   Row missing + legacy → user pre-dates migration 005; insert default row
//
// Also reads display_name from profiles and merges it with prefs.
// Never throws — auth must never be blocked by a prefs error.
// ─────────────────────────────────────────────────────────────────────────────
export async function ensureUserPreferences(userId) {
  if (!hasSupabase || !userId) return;

  const { error } = await supabase
    .from(TABLE)
    .upsert({ user_id: userId }, { onConflict: "user_id", ignoreDuplicates: true });

  if (error) {
    console.warn("[userService] ensureUserPreferences error:", error.message);
    // Do not throw — auth must continue even if this fails
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// getDisplayName
// Reads display_name from profiles. Returns null if not set.
// ─────────────────────────────────────────────────────────────────────────────
export async function getDisplayName(userId) {
  if (!hasSupabase || !userId) return null;

  const { data, error } = await supabase
    .from("profiles")
    .select("display_name")
    .eq("id", userId)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // profile row not found — non-fatal
    throw new Error(`getDisplayName: ${error.message}`);
  }
  return data?.display_name ?? null;
}
